import { Applicant, ProviderData, ThirdPartyData } from 'store/dashboard/types'
import { Box, Divider, Paper, Stack, Tooltip, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import JoinInnerIcon from '@mui/icons-material/JoinInner'
import { ReactComponent as RevenueIcon } from 'assets/images/revenue.svg'
import { RiFocus2Line } from 'react-icons/ri'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import styles from './ApplicantCard.module.scss'
import theme from 'containers/app/theme'
import { useState } from 'react'
import React from 'react'

type Props = {
  applicantList: Applicant[]
  thirdPartyData: ThirdPartyData | undefined
  target?: boolean
  multisectorSubgroups: string[]
}

export default function ApplicantCard({ multisectorSubgroups, applicantList, thirdPartyData, target }: Props) {
  // const tpdWinner = thirdPartyData?.owler

  let tpdWinner: ProviderData | null = null

  if (thirdPartyData) {
    if (thirdPartyData.owler.length) {
      tpdWinner = thirdPartyData.owler[0]
    } else if (thirdPartyData.yfinance_nasdaq.length) {
      tpdWinner = thirdPartyData.yfinance_nasdaq[0]
    } else if (thirdPartyData.yfinance_eu.length) {
      tpdWinner = thirdPartyData.yfinance_eu[0]
    }
  }

  const [selectedPosition, setSelectedPosition] = useState<number>(0)
  const applicant = applicantList[selectedPosition]
  const sectorNumbers = applicantList.map(applicant => multisectorSubgroups.indexOf(applicant.subgroup) + 1)

  const Title = () => {
    return (
      <Stack direction="row" className={styles.titleContainer} justifyContent="space-between">
        <Typography className={styles.title}>{applicant.applicant_original_name}</Typography>
        {/* <Avatar sx={{ backgroundColor: 'primary.main', height: '20px', width: '20px' }}>
          <AddIcon fontSize="small" />
        </Avatar> */}
      </Stack>
    )
  }

  const cardStyles = { backgroundColor: target ? 'primary.main' : 'transparent', borderRadius: '20px', borderColor: '#e8e8e8' }

  const iconBoxStyles = {
    minWidth: '30px',
    display: 'flex',
    alignItems: 'flex-end', // o 'center' si prefieres
  }

  const parseNumber = (value: any): number => {
    if (typeof value === 'number') return value
    if (typeof value === 'string') {
      // Fix cases like 3,07394E+11
      const cleaned = value.replace(',', '.').replace(/\s/g, '')
      const parsed = parseFloat(cleaned)
      return isNaN(parsed) ? 0 : parsed
    }
    return 0
  }

  const getRevPerEmployee = (): number | null => {
    const tpd = tpdWinner
    const revenue = parseNumber(tpd?.revenue)
    const employees = parseNumber(tpd?.employee)
    if (revenue > 0 && employees > 0) {
      return Math.round(revenue / employees / 1000)
    }
    return null
  }

  const getColor = (value: number): string => {
    if (target) return 'white'
    if (value <= 99) return 'red'
    if (value <= 249) return 'lightgrey'
    if (value <= 499) return theme.palette.primary.light
    return theme.palette.primary.main
  }

  const revEmpl = getRevPerEmployee()

  const purePlayRatio = applicant.whales_patent_in_percent && Number(applicant.whales_patent_in_percent.toFixed(2))
  const getPurePlayRatioColor = (value: number): string => {
    if (target) return 'white'
    if (value <= 5) return 'lightgrey'
    if (value <= 20) return theme.palette.primary.light
    return theme.palette.primary.main
  }

  return (
    <div className={styles.base}>
      <Paper variant="outlined" className={`${styles.cardContainer} ${target && styles.target}`} sx={cardStyles}>
        <Box sx={{ p: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ maxWidth: 345 }}>
            <>
              <Stack className={styles.stack2}>
                <Title />

                <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between" sx={{ fontSize: '34px' }}>
                  <Box sx={iconBoxStyles}>
                    {applicant.intersection_score_sum_normalized > 1 && (
                      <Tooltip title={`IP Similarity normalized: ${applicant.intersection_score_sum_normalized.toFixed(2)}`}>
                        <JoinInnerIcon sx={{ color: target ? 'white' : 'primary.main', fontSize: 'inherit' }} />
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={iconBoxStyles}>
                    {tpdWinner && revEmpl && (
                      <RevenueIcon
                        style={{
                          color: getColor(revEmpl),
                          fontSize: 'inherit',
                          width: '1em', // Safari needs width and height to render SVG correctly
                          height: '1em',
                        }}
                      />
                    )}
                  </Box>

                  <Box sx={iconBoxStyles}>
                    <Tooltip title={`Pure Play %: ${purePlayRatio}`}>
                      <Box display={'flex'}>
                        <RiFocus2Line style={{ color: getPurePlayRatioColor(purePlayRatio), fontSize: 'inherit' }} />
                      </Box>
                    </Tooltip>
                  </Box>
                  <Box sx={iconBoxStyles}>
                    {tpdWinner && tpdWinner.ticker && (
                      <Tooltip title={`Quoted company`}>
                        <AccountBalanceIcon sx={{ color: target ? 'white' : 'grey', fontSize: '28px' }} />
                      </Tooltip>
                    )}
                  </Box>
                </Stack>
              </Stack>
            </>
          </Stack>
        </Box>
        <Divider />
        <Box sx={{ p: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ fontSize: '14px', color: target ? 'white' : 'primary.main' }}>
            Sectors ({sectorNumbers.length}):{' '}
            {sectorNumbers.map((sector, index) => (
              <span key={index} onClick={() => setSelectedPosition(index)} style={{ cursor: 'pointer' }}>
                {index === selectedPosition ? (
                  <Tooltip title={applicant.subgroup}>
                    <span>
                      <strong>{sector}</strong>
                    </span>
                  </Tooltip>
                ) : (
                  sector
                )}
                {index < sectorNumbers.length - 1 && ', '}
              </span>
            ))}
          </Stack>
        </Box>
      </Paper>
    </div>
  )
}
